import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Clock,
  Settings,
  AlertTriangle,
  Bookmark,
  Share2,
  Droplets,
  WifiOff,
  CheckCircle2,
  Info,
} from 'lucide-react';
import type {
  TripEndpointRow,
  OSRMRoute,
  RouteWaypoint,
  HourlyWeatherSample,
  WeatherLayer,
  WeatherAlert,
  UnitsMode,
  SavedRouteEntry,
  AvoidOptions,
} from './types';
import { sampleRoute, alongRouteDistanceM } from './utils/geo';
import { parametricAnchorIndices } from './utils/tickerPins';
import { fetchOSRMRoute } from './services/osrm';
import { fetchBatchHourlyForecast, nearestHourly, coordCacheKey } from './services/openMeteo';
import { fetchNwsAlerts, worstSeverity, severityColor } from './services/weatherAlerts';
import {
  listSavedRoutes,
  deleteSavedRoute,
  purgeRemovedBuiltinExampleRoute,
  saveRoute as persistOriginDest,
  savedRouteLabel,
} from './services/savedRoutes';
import { isPlaceFallback, reverseGeocode, searchPlaces } from './services/places';
import {
  getDefaultUnits,
  saveUnitsPreference,
  formatDistance,
  formatDuration,
  formatClockTime,
} from './utils/units';
import { buildTripShareMessage } from './utils/shareTrip';
import { precipitationForecastTileUrl } from './features/weather/precipForecastTiles';
import {
  buildDepartureCandidates,
  departureTimesForSlider,
  pickBestDeparture,
} from './features/departure/bestDeparture';
import { parseGpx } from './features/gpx/parseGpx';
import { computeElevationStats, fetchElevation, thinElevationPoints, withFetchedElevations, type ElevationStats } from './features/gpx/elevation';
import {
  hourlyMapFromCache,
  hourlyMapToRecord,
  loadOfflineForecast,
  saveOfflineForecast,
} from './features/offline/offlineForecast';
import {
  ensureNotificationPermission,
  loadWatches,
  saveWatches,
  tickReminders,
  watchFromCurrentTrip,
} from './features/tripAlerts/webReminders';

// Components
import { MapView } from './components/MapView';
import { SearchCard } from './components/SearchCard';
import { RouteCards } from './components/RouteCards';
import { getRouteTollInfo } from './utils/tolls';
import { DriveChart } from './components/DriveChart';
import { DepartureTimeline } from './components/DepartureTimeline';
import { StopDwellPickerModal } from './components/StopDwellPickerModal';
import { TickerMetricToggle } from './components/TickerMetricToggle';
import { TickerWeatherDetail } from './components/TickerWeatherDetail';
import { RouteAlertsModal } from './components/RouteAlertsModal';
import { SavedRoutesDrawer } from './components/SavedRoutesDrawer';
import {
  HazardAlertIcon,
  ThunderstormIcon,
  RainIcon,
  SnowIcon,
  WindIcon,
  ClockEtaIcon,
  SunIcon,
} from './components/icons/OnTheRouteIcons';
import { SettingsModal } from './components/SettingsModal';
import { OnboardingModal } from './components/OnboardingModal';

// Responsive breakpoint
const DESKTOP_MIN_WIDTH = 900;
const DETAIL_FLOAT_MIN_WIDTH = 1280;

export const App: React.FC = () => {
  // Theme & Appearance
  const [themeMode, setThemeMode] = useState<'system' | 'light' | 'dark'>('system');
  const [systemIsDark, setSystemIsDark] = useState(() =>
    window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)').matches : false,
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemIsDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const isDark = themeMode === 'system' ? systemIsDark : themeMode === 'dark';

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
    document.documentElement.classList.toggle('light', !isDark);
  }, [isDark]);

  // Viewport dimensions
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isDesktop = windowWidth >= DESKTOP_MIN_WIDTH;
  const useDetailFloat = windowWidth >= DETAIL_FLOAT_MIN_WIDTH;

  // Units
  const [unitsMode, setUnitsMode] = useState<UnitsMode>(getDefaultUnits);
  const handleToggleUnits = () => {
    const next = unitsMode === 'imperial' ? 'metric' : 'imperial';
    setUnitsMode(next);
    saveUnitsPreference(next);
  };

  // In-app Toast notifications
  const [toast, setToast] = useState<{ message: string; type: 'info' | 'success' | 'warning' } | null>(null);
  const showToast = useCallback((message: string, type: 'info' | 'success' | 'warning' = 'info') => {
    setToast({ message, type });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(timer);
  }, [toast]);

  // Network online / offline status
  const [isOffline, setIsOffline] = useState(() => (typeof navigator !== 'undefined' ? !navigator.onLine : false));
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      showToast('Internet connection restored.', 'success');
    };
    const handleOffline = () => {
      setIsOffline(true);
      showToast('You are offline. Live updates paused.', 'warning');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [showToast]);

  // Onboarding
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return !localStorage.getItem('ontheway_onboarded_v1');
  });

  const handleDismissOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem('ontheway_onboarded_v1', '1');
  };

  useEffect(() => {
    purgeRemovedBuiltinExampleRoute();
  }, []);

  // Trip Endpoints
  const [endpoints, setEndpoints] = useState<TripEndpointRow[]>(() => [
    {
      id: 'ep-start',
      query: 'Los Angeles, CA',
      place: {
        id: 'place-la',
        displayName: 'Los Angeles, CA, USA',
        shortName: 'Los Angeles, CA',
        coordinate: { latitude: 34.0522, longitude: -118.2437 },
      },
      dwellSec: 0,
    },
    {
      id: 'ep-dest',
      query: 'Las Vegas, NV',
      place: {
        id: 'place-vegas',
        displayName: 'Las Vegas, NV, USA',
        shortName: 'Las Vegas, NV',
        coordinate: { latitude: 36.1147, longitude: -115.172 },
      },
      dwellSec: 0,
    },
  ]);

  // Departure Time
  const [confirmedDepartureTime, setConfirmedDepartureTime] = useState<Date>(() => new Date());
  const [previewDepartureTime, setPreviewDepartureTime] = useState<Date | null>(null);
  const effectiveDeparture = previewDepartureTime || confirmedDepartureTime;

  // Check if departure exceeds Open-Meteo 16-day forecast horizon
  const isBeyondForecastHorizon = useMemo(() => {
    return effectiveDeparture.getTime() > Date.now() + 16 * 86_400_000;
  }, [effectiveDeparture]);

  const routeRequestIdRef = useRef(0);
  const weatherFetchIdRef = useRef(0);

  // Test helpers for screenshot automation
  useEffect(() => {
    (window as any).__SET_DEPARTURE_TEST__ = (d: Date) => {
      setConfirmedDepartureTime(new Date(d));
      setPreviewDepartureTime(null);
    };
    (window as any).__RESET_TO_START_STATE__ = () => {
      routeRequestIdRef.current++;
      setShowOnboarding(false);
      setEndpoints([
        {
          id: 'ep-start',
          query: 'Los Angeles, CA',
          place: {
            id: 'place-la',
            displayName: 'Los Angeles, CA, USA',
            shortName: 'Los Angeles, CA',
            coordinate: { latitude: 34.0522, longitude: -118.2437 },
          },
          dwellSec: 0,
        },
        {
          id: 'ep-dest',
          query: '',
          place: null,
          dwellSec: 0,
        },
      ]);
      setRoutes([]);
      setWaypoints([]);
      setSelectedRouteIndex(0);
      setIsBuildingRoute(false);
      setWeatherLoadingProgress(null);
      setAlertsByWaypoint(new Map());
      setAllRouteAlerts([]);
    };
    (window as any).__DISMISS_ONBOARDING__ = () => {
      setShowOnboarding(false);
      localStorage.setItem('ontheway_onboarded_v1', '1');
    };
  }, []);

  // Routes & Alternates
  const [routes, setRoutes] = useState<OSRMRoute[]>([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [avoidOptions, setAvoidOptions] = useState<AvoidOptions>({ avoidTolls: false, avoidHighways: false });
  const [isBuildingRoute, setIsBuildingRoute] = useState(false);
  const [routeError, setRouteError] = useState<string | null>(null);

  // Weather along route
  const [waypoints, setWaypoints] = useState<RouteWaypoint[]>([]);
  const [rawWeatherByCoord, setRawWeatherByCoord] = useState<Map<string, HourlyWeatherSample[]>>(new Map());

  useEffect(() => {
    (window as any).__HAS_ROUTE__ = routes.length > 0 && waypoints.length > 0;
  }, [routes, waypoints]);

  const [weatherLoadingProgress, setWeatherLoadingProgress] = useState<number | null>(null);
  const [activeLayer, setActiveLayer] = useState<WeatherLayer>('temp');

  const [precipEnabled, setPrecipEnabled] = useState(false);
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderLeadMin, setReminderLeadMin] = useState(30);
  const [elevationStats, setElevationStats] = useState<ElevationStats | null>(null);
  const [offlineCachedAt, setOfflineCachedAt] = useState<string | null>(null);

  // Alerts
  const [alertsByWaypoint, setAlertsByWaypoint] = useState<Map<string, WeatherAlert[]>>(new Map());
  const [allRouteAlerts, setAllRouteAlerts] = useState<WeatherAlert[]>([]);

  // Scrub & Selection State
  const [scrubProgress, setScrubProgress] = useState<number | null>(null);
  const [selectedWaypointId, setSelectedWaypointId] = useState<string | null>(null);

  // Modals
  const [showDepartureModal, setShowDepartureModal] = useState(false);
  const [dwellPickerIndex, setDwellPickerIndex] = useState<number | null>(null);
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [showSavedDrawer, setShowSavedDrawer] = useState(false);
  const [, setSavedRoutesVersion] = useState(0);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [mobileToggleTop, setMobileToggleTop] = useState(168);
  const [localityMiss, setLocalityMiss] = useState<Set<string>>(() => new Set());

  // Active route
  const activeRoute = routes[selectedRouteIndex];
  const selectedWaypoint = selectedWaypointId
    ? waypoints.find((w) => w.id === selectedWaypointId) ?? null
    : null;

  useEffect(() => {
    if (!selectedWaypoint || !isPlaceFallback(selectedWaypoint.placeName)) return;
    if (localityMiss.has(selectedWaypoint.id)) return;
    const fetchId = weatherFetchIdRef.current;
    const wp = selectedWaypoint;
    let cancelled = false;
    void (async () => {
      const name = await reverseGeocode(wp.coordinate, wp.distanceM);
      if (cancelled || weatherFetchIdRef.current !== fetchId) return;
      if (!isPlaceFallback(name)) {
        setWaypoints((current) =>
          current.map((w) => (w.id === wp.id ? { ...w, placeName: name } : w)),
        );
        return;
      }
      setLocalityMiss((prev) => {
        if (prev.has(wp.id)) return prev;
        const next = new Set(prev);
        next.add(wp.id);
        return next;
      });
      setWaypoints((current) =>
        current.map((w) => (w.id === wp.id && isPlaceFallback(w.placeName) ? { ...w, placeName: name } : w)),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedWaypoint, localityMiss]);

  useEffect(() => {
    if (isDesktop) return;
    const el = document.getElementById('map-search-card');
    if (!el) return;
    const update = () => setMobileToggleTop(Math.round(el.getBoundingClientRect().bottom + 8));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [isDesktop, endpoints, isBuildingRoute, routes.length, selectedWaypointId]);

  const applyPlaceNames = useCallback((sampled: RouteWaypoint[], fetchId: number) => {
    void (async () => {
      const anchors = parametricAnchorIndices(sampled.length);
      const rest = sampled.map((_, i) => i).filter((i) => !anchors.includes(i));
      for (const i of [...anchors, ...rest]) {
        const wp = sampled[i];
        if (!wp) continue;
        if (weatherFetchIdRef.current !== fetchId) return;
        const name = await reverseGeocode(wp.coordinate, wp.distanceM);
        if (weatherFetchIdRef.current !== fetchId) return;
        if (!name) continue;
        setWaypoints((current) =>
          current.map((w) => {
            if (w.id !== wp.id) return w;
            if (!isPlaceFallback(w.placeName) && isPlaceFallback(name)) return w;
            return { ...w, placeName: name };
          }),
        );
      }
    })();
  }, []);

  const loadWeatherForWaypoints = useCallback(async (sampled: RouteWaypoint[], fetchId: number) => {
    setWeatherLoadingProgress(0.25);
    try {
      const forecastMap = await fetchBatchHourlyForecast(
        sampled.map((w) => w.coordinate),
        16,
      );
      if (weatherFetchIdRef.current !== fetchId) return;
      setRawWeatherByCoord(forecastMap);
      setWeatherLoadingProgress(0.7);

      const alertsMap = new Map<string, WeatherAlert[]>();
      const alertsList: WeatherAlert[] = [];
      const seen = new Set<string>();
      await Promise.all(
        sampled.map(async (wp) => {
          const alerts = await fetchNwsAlerts(wp.coordinate.latitude, wp.coordinate.longitude);
          if (weatherFetchIdRef.current !== fetchId) return;
          if (alerts.length === 0) return;
          alertsMap.set(wp.id, alerts);
          alerts.forEach((a) => {
            const sig = `${a.event}::${a.headline}`;
            if (!seen.has(sig)) {
              seen.add(sig);
              alertsList.push(a);
            }
          });
        }),
      );
      if (weatherFetchIdRef.current !== fetchId) return;
      setAlertsByWaypoint(alertsMap);
      setAllRouteAlerts(alertsList);
      setWeatherLoadingProgress(1);
      setTimeout(() => {
        if (weatherFetchIdRef.current === fetchId) setWeatherLoadingProgress(null);
      }, 400);
    } catch {
      if (weatherFetchIdRef.current !== fetchId) return;
      setRawWeatherByCoord(new Map());
      setWeatherLoadingProgress(null);
    }
  }, []);

  const handleSelectRouteIndex = useCallback(
    (idx: number) => {
      setSelectedRouteIndex(idx);
      setSelectedWaypointId(null);
      setLocalityMiss(new Set());
      const chosen = routes[idx];
      if (!chosen) return;
      const sampled = sampleRoute(chosen.geometry.coordinates, chosen.duration, chosen.distance);
      setWaypoints(sampled);
      setRawWeatherByCoord(new Map());
      setAlertsByWaypoint(new Map());
      const fetchId = ++weatherFetchIdRef.current;
      applyPlaceNames(sampled, fetchId);
      void loadWeatherForWaypoints(sampled, fetchId);
    },
    [routes, loadWeatherForWaypoints, applyPlaceNames],
  );

  useEffect(() => {
    (window as any).__SELECT_ROUTE_INDEX__ = (idx: number) => {
      handleSelectRouteIndex(idx);
    };
  }, [handleSelectRouteIndex]);

  // Build Route Function
  const handleBuildRoute = useCallback(async (customEndpoints?: TripEndpointRow[]) => {
    let currentEndpoints = customEndpoints || endpoints;
    const missing = currentEndpoints
      .map((e, idx) => (e.query.trim().length > 0 && e.place === null ? idx : -1))
      .filter((idx) => idx !== -1);

    if (missing.length > 0) {
      const resolved = [...currentEndpoints];
      await Promise.all(
        missing.map(async (idx) => {
          const results = await searchPlaces(resolved[idx]!.query.trim());
          if (results.length > 0) {
            resolved[idx] = {
              ...resolved[idx]!,
              query: results[0]!.shortName,
              place: results[0]!,
            };
          }
        }),
      );
      setEndpoints(resolved);
      currentEndpoints = resolved;
    }

    const validEndpoints = currentEndpoints.filter((e) => e.place !== null);
    if (validEndpoints.length < 2) return;

    const reqId = ++routeRequestIdRef.current;
    setIsBuildingRoute(true);
    setRouteError(null);
    setWeatherLoadingProgress(0);

    try {
      const coords = validEndpoints.map((e) => e.place!.coordinate);
      const osrmRoutes = await fetchOSRMRoute(coords, { alternatives: validEndpoints.length === 2 });

      if (routeRequestIdRef.current !== reqId) return;
      if (osrmRoutes.length === 0) {
        throw new Error('No route returned by router.');
      }

      let sortedRoutes = [...osrmRoutes];
      if (avoidOptions.avoidTolls) {
        sortedRoutes.sort((a, b) => {
          const aTolls = getRouteTollInfo(a).hasMandatoryTolls ? 1 : 0;
          const bTolls = getRouteTollInfo(b).hasMandatoryTolls ? 1 : 0;
          return aTolls - bTolls;
        });
      }
      setRoutes(sortedRoutes);
      setSelectedRouteIndex(0);

      // Sample route
      const chosen = osrmRoutes[0]!;
      const polyline = chosen.geometry.coordinates;
      const sampled = sampleRoute(polyline, chosen.duration, chosen.distance);
      if (routeRequestIdRef.current !== reqId) return;
      setWaypoints(sampled);
      setLocalityMiss(new Set());

      // Fetch batch hourly forecasts from Open-Meteo (16 days coverage)
      setWeatherLoadingProgress(0.3);
      const sampleCoords = sampled.map((w) => w.coordinate);
      const fetchId = ++weatherFetchIdRef.current;
      applyPlaceNames(sampled, fetchId);
      const forecastMap = await fetchBatchHourlyForecast(sampleCoords, 16);
      if (routeRequestIdRef.current !== reqId || weatherFetchIdRef.current !== fetchId) return;
      setRawWeatherByCoord(forecastMap);
      setWeatherLoadingProgress(0.8);

      // Fetch NWS alerts for waypoints
      const alertsMap = new Map<string, WeatherAlert[]>();
      const alertsList: WeatherAlert[] = [];
      const seenAlertSignatures = new Set<string>();

      await Promise.all(
        sampled.map(async (wp) => {
          const alerts = await fetchNwsAlerts(wp.coordinate.latitude, wp.coordinate.longitude);
          if (alerts.length > 0) {
            alertsMap.set(wp.id, alerts);
            alerts.forEach((a) => {
              const sig = `${a.event}::${a.headline}`;
              if (!seenAlertSignatures.has(sig)) {
                seenAlertSignatures.add(sig);
                alertsList.push(a);
              }
            });
          }
        }),
      );

      if (routeRequestIdRef.current !== reqId || weatherFetchIdRef.current !== fetchId) return;
      setAlertsByWaypoint(alertsMap);
      setAllRouteAlerts(alertsList);
      setOfflineCachedAt(null);
      saveOfflineForecast({
        version: 1,
        cachedAtMs: Date.now(),
        cachedAtStr: new Date().toLocaleString(),
        departureIso: confirmedDepartureTime.toISOString(),
        routes: osrmRoutes,
        selectedRouteIndex: 0,
        waypoints: sampled,
        tripEndpoints: endpoints,
        hourlyByCoordKey: hourlyMapToRecord(forecastMap),
        alerts: alertsList,
      });
      setWeatherLoadingProgress(1.0);
      setTimeout(() => {
        if (routeRequestIdRef.current === reqId) setWeatherLoadingProgress(null);
      }, 500);
    } catch (err: any) {
      if (routeRequestIdRef.current !== reqId) return;
      const isNetwork = !navigator.onLine || err.message?.includes('fetch') || err.message?.includes('NetworkError') || err.message?.includes('Failed to fetch') || err.message?.includes('network');
      if (isNetwork) {
        const cache = loadOfflineForecast();
        if (cache) {
          setRoutes(cache.routes);
          setSelectedRouteIndex(cache.selectedRouteIndex);
          setWaypoints(cache.waypoints);
          setEndpoints(cache.tripEndpoints);
          setRawWeatherByCoord(hourlyMapFromCache(cache));
          setAllRouteAlerts(cache.alerts);
          setConfirmedDepartureTime(new Date(cache.departureIso));
          setOfflineCachedAt(cache.cachedAtStr);
          setIsOffline(true);
          setRouteError(null);
          showToast(`Showing cached forecast from ${cache.cachedAtStr}.`, 'warning');
        } else {
          setIsOffline(true);
          setRouteError('You appear to be offline. Please check your internet connection.');
        }
      } else {
        setRouteError(err.message || 'Failed to build route.');
      }
      setWeatherLoadingProgress(null);
    } finally {
      if (routeRequestIdRef.current === reqId) {
        setIsBuildingRoute(false);
      }
    }
  }, [endpoints, confirmedDepartureTime, showToast, avoidOptions, applyPlaceNames]);

  // Auto-build 2-endpoint trip on initial mount
  useEffect(() => {
    if (endpoints.length === 2 && endpoints[0]!.place && endpoints[1]!.place && routes.length === 0) {
      handleBuildRoute();
    }
  }, []);

  const precipTileUrl = useMemo(() => {
    if (!precipEnabled) return null;
    return precipitationForecastTileUrl(effectiveDeparture, new Date());
  }, [precipEnabled, effectiveDeparture]);

  // Sliced weather samples at each waypoint's ETA (dwell included)
  const waypointWeather = useMemo(() => {
    const map = new Map<string, HourlyWeatherSample>();
    if (waypoints.length === 0 || rawWeatherByCoord.size === 0) return map;

    // Calculate total dwell before each waypoint
    const activePolyline = activeRoute?.geometry.coordinates || [];
    const stopAlongDistances = endpoints.map((ep) =>
      ep.place ? alongRouteDistanceM(activePolyline, ep.place.coordinate) : 0,
    );

    waypoints.forEach((wp) => {
      const coordKey = coordCacheKey(wp.coordinate);
      const series = rawWeatherByCoord.get(coordKey);
      if (!series || series.length === 0) return;

      // Calculate dwell before this waypoint
      let dwellBeforeSec = 0;
      for (let j = 1; j < endpoints.length - 1; j++) {
        const stopDist = stopAlongDistances[j] || 0;
        if (stopDist < wp.distanceM - 80) {
          dwellBeforeSec += endpoints[j]?.dwellSec || 0;
        }
      }

      const etaMs = effectiveDeparture.getTime() + (wp.travelTimeSec + dwellBeforeSec) * 1000;
      const targetDate = new Date(etaMs);
      const match = nearestHourly(series, targetDate);
      if (match) {
        map.set(wp.id, match);
      }
    });

    return map;
  }, [waypoints, rawWeatherByCoord, effectiveDeparture, endpoints, activeRoute]);

  const bestLeave = useMemo(() => {
    if (!waypoints.length || rawWeatherByCoord.size === 0) return null;
    const times = departureTimesForSlider(effectiveDeparture);
    const waypointForecasts = waypoints.map((wp) => ({
      travelTimeSec: wp.travelTimeSec,
      hourly: (rawWeatherByCoord.get(coordCacheKey(wp.coordinate)) ?? []).map((s) => ({
        time: s.time,
        precipitationMm: s.precipitationMm,
        precipProbability: s.precipProbability,
      })),
    }));
    return pickBestDeparture(buildDepartureCandidates(times, waypointForecasts), effectiveDeparture);
  }, [waypoints, rawWeatherByCoord, effectiveDeparture]);

  // Overall route severity
  const worstRouteSeverity = useMemo(() => {
    return worstSeverity(allRouteAlerts);
  }, [allRouteAlerts]);

  // Add Via Stop
  const handleAddStop = () => {
    if (endpoints.length >= 5) return;
    const newStop: TripEndpointRow = {
      id: `ep-${Date.now()}`,
      query: '',
      place: null,
      dwellSec: 0,
    };
    // Insert before the last element (destination)
    const next = [...endpoints.slice(0, endpoints.length - 1), newStop, endpoints[endpoints.length - 1]!];
    setEndpoints(next);
  };

  // Remove Via Stop
  const handleRemoveStop = (index: number) => {
    const next = endpoints.filter((_, i) => i !== index);
    setEndpoints(next);
  };

  // Load Saved Route
  const handleLoadSavedRoute = (entry: SavedRouteEntry) => {
    const newEndpoints: TripEndpointRow[] = [
      {
        id: `ep-${Date.now()}-start`,
        query: entry.start.displayName.split(',')[0] || entry.start.displayName,
        place: {
          id: `place-start`,
          displayName: entry.start.displayName,
          shortName: entry.start.displayName.split(',')[0] || entry.start.displayName,
          coordinate: { latitude: entry.start.latitude, longitude: entry.start.longitude },
        },
        dwellSec: 0,
      },
      {
        id: `ep-${Date.now()}-dest`,
        query: entry.end.displayName.split(',')[0] || entry.end.displayName,
        place: {
          id: `place-dest`,
          displayName: entry.end.displayName,
          shortName: entry.end.displayName.split(',')[0] || entry.end.displayName,
          coordinate: { latitude: entry.end.latitude, longitude: entry.end.longitude },
        },
        dwellSec: 0,
      },
    ];
    setSelectedWaypointId(null);
    setRawWeatherByCoord(new Map());
    setRoutes([]);
    setWaypoints([]);
    setEndpoints(newEndpoints);
    void handleBuildRoute(newEndpoints);
  };

  const handleSaveOriginDest = () => {
    const startPlace = endpoints[0]?.place;
    const destPlace = endpoints[endpoints.length - 1]?.place;
    if (!startPlace || !destPlace) {
      showToast('Enter origin and destination first.', 'warning');
      return;
    }
    const result = persistOriginDest({
      start: {
        displayName: startPlace.displayName,
        latitude: startPlace.coordinate.latitude,
        longitude: startPlace.coordinate.longitude,
      },
      end: {
        displayName: destPlace.displayName,
        latitude: destPlace.coordinate.latitude,
        longitude: destPlace.coordinate.longitude,
      },
    });
    if (!result.ok) {
      showToast(
        result.reason === 'storage' ? 'Could not save on this browser.' : 'Could not save this pair.',
        'warning',
      );
      return;
    }
    setSavedRoutesVersion((v) => v + 1);
    showToast(
      result.duplicate
        ? `Updated ${savedRouteLabel(result.entry)} on this browser.`
        : `Saved ${savedRouteLabel(result.entry)} on this browser.`,
      'success',
    );
  };

  // Total via dwell seconds
  const totalViaDwellSec = useMemo(() => {
    return endpoints.slice(1, endpoints.length - 1).reduce((sum, e) => sum + (e.dwellSec || 0), 0);
  }, [endpoints]);

  // Arrival time (Departure + drive duration + dwell)
  const arrivalTime = useMemo(() => {
    if (!activeRoute) return effectiveDeparture;
    return new Date(effectiveDeparture.getTime() + (activeRoute.duration + totalViaDwellSec) * 1000);
  }, [effectiveDeparture, activeRoute, totalViaDwellSec]);

  const renderVerdictIcon = (type: string, color?: string, size = 18) => {
    switch (type) {
      case 'alert':
        return <HazardAlertIcon size={size} color={color || 'var(--danger)'} />;
      case 'thunder':
        return <ThunderstormIcon size={size} />;
      case 'rain':
        return <RainIcon size={size} />;
      case 'snow':
        return <SnowIcon size={size} />;
      case 'wind':
        return <WindIcon size={size} />;
      case 'horizon':
        return <ClockEtaIcon size={size} color="var(--sun)" />;
      case 'clear':
      default:
        return <SunIcon size={size} />;
    }
  };

  // Verdict calculation derived from actual sampled precipitation, wind, and alerts
  const verdictInfo = useMemo(() => {
    if (waypoints.length === 0) return null;

    // 1. Check active route weather alerts (NWS bulletins)
    if (allRouteAlerts.length > 0) {
      const worst = allRouteAlerts[0]!;
      const matchingWp = waypoints.find((w) =>
        (alertsByWaypoint.get(w.id) || []).some((a) => a.event === worst.event),
      );
      const town = matchingWp?.placeName?.split(',')[0] || 'along route';
      const color = severityColor(worst.severity);
      return {
        type: 'alert',
        title: `${worst.event} near ${town}`,
        subtitle: 'Active NWS weather alert · drive with care',
        iconType: 'alert',
        bg: worst.severity === 'Extreme' || worst.severity === 'Severe' ? 'rgba(255,59,48,0.15)' : 'var(--sunTint)',
        color,
      };
    }

    // 2. Check rain / precipitation from sampled forecasts
    const rainPoints = waypoints.filter((w) => {
      const sample = waypointWeather.get(w.id);
      if (!sample) return false;
      const pop = sample.precipProbability ?? 0;
      const code = sample.weatherCode ?? 0;
      const isRainCode = [51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82, 95, 96, 99].includes(code);
      return pop >= 15 || isRainCode;
    });

    if (rainPoints.length > 0) {
      let maxPopPoint = rainPoints[0]!;
      let maxPop = waypointWeather.get(maxPopPoint.id)?.precipProbability ?? 0;
      for (const p of rainPoints) {
        const pop = waypointWeather.get(p.id)?.precipProbability ?? 0;
        if (pop > maxPop) {
          maxPop = pop;
          maxPopPoint = p;
        }
      }

      const town = maxPopPoint.placeName ? maxPopPoint.placeName.split(',')[0] : 'along route';
      const etaStr = formatClockTime(
        new Date(effectiveDeparture.getTime() + maxPopPoint.travelTimeSec * 1000),
      );
      const isThunder = [95, 96, 99].includes(waypointWeather.get(maxPopPoint.id)?.weatherCode ?? 0);
      const isHeavy = maxPop >= 50 || isThunder;

      return {
        type: 'rain',
        title: isHeavy ? (isThunder ? `Thunderstorms near ${town}` : `Rain near ${town}`) : `Light rain near ${town}`,
        subtitle: `Around ${etaStr} (${maxPop}% chance) · drive with care`,
        iconType: isThunder ? 'thunder' : 'rain',
        bg: 'var(--rainTint)',
        color: 'var(--rain)',
      };
    }

    // 3. Check snow / winter weather
    const snowPoints = waypoints.filter((w) => {
      const code = waypointWeather.get(w.id)?.weatherCode ?? 0;
      return [71, 73, 75, 77, 85, 86].includes(code);
    });

    if (snowPoints.length > 0) {
      const firstSnow = snowPoints[0]!;
      const town = firstSnow.placeName ? firstSnow.placeName.split(',')[0] : 'along route';
      const etaStr = formatClockTime(
        new Date(effectiveDeparture.getTime() + firstSnow.travelTimeSec * 1000),
      );
      return {
        type: 'snow',
        title: `Snow near ${town}`,
        subtitle: `Around ${etaStr} · check road conditions`,
        iconType: 'snow',
        bg: 'var(--rainTint)',
        color: 'var(--rain)',
      };
    }

    // 4. Check high wind / gusts
    const windPoints = waypoints.filter((w) => {
      const sample = waypointWeather.get(w.id);
      return (sample?.windKmh ?? 0) >= 38 || (sample?.windGustsKmh ?? 0) >= 48;
    });

    if (windPoints.length > 0) {
      const firstWind = windPoints[0]!;
      const town = firstWind.placeName ? firstWind.placeName.split(',')[0] : 'along route';
      const gusts = Math.round(
        unitsMode === 'imperial'
          ? (waypointWeather.get(firstWind.id)?.windGustsKmh ?? 45) * 0.621371
          : waypointWeather.get(firstWind.id)?.windGustsKmh ?? 45,
      );
      const unit = unitsMode === 'imperial' ? 'mph' : 'km/h';
      return {
        type: 'wind',
        title: `Windy conditions near ${town}`,
        subtitle: `Gusts up to ${gusts} ${unit} · drive with care`,
        iconType: 'wind',
        bg: 'var(--sunTint)',
        color: 'var(--sun)',
      };
    }

    // 5. Check if beyond forecast horizon
    if (isBeyondForecastHorizon) {
      return {
        type: 'horizon',
        title: 'Beyond 16-day forecast horizon',
        subtitle: 'Showing latest forecast; select earlier departure for live weather',
        iconType: 'horizon',
        bg: 'var(--sunTint)',
        color: 'var(--sun)',
      };
    }

    // 6. Otherwise: Clear
    const destName = endpoints[endpoints.length - 1]?.place?.shortName || 'destination';
    return {
      type: 'clear',
      title: 'Clear all the way',
      subtitle: `Arrive ${formatClockTime(arrivalTime)} in ${destName}`,
      iconType: 'clear',
      bg: 'var(--sunTint)',
      color: 'var(--sun)',
    };
  }, [
    waypoints,
    waypointWeather,
    effectiveDeparture,
    arrivalTime,
    allRouteAlerts,
    alertsByWaypoint,
    unitsMode,
    isBeyondForecastHorizon,
    endpoints,
  ]);

  // Share trip handler
  const handleShare = () => {
    if (!activeRoute) return;
    const text = buildTripShareMessage({
      startName: endpoints[0]?.place?.shortName || 'Start',
      endName: endpoints[endpoints.length - 1]?.place?.shortName || 'Destination',
      distanceMeters: activeRoute.distance,
      durationMin: Math.round(activeRoute.duration / 60),
      imperial: unitsMode === 'imperial',
      alertSummary: allRouteAlerts[0]?.event,
    });

    if (navigator.share) {
      navigator.share({ title: 'OnTheRoute Drive', text, url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
      showToast('Trip summary copied to clipboard!', 'success');
    }
  };

  const handleImportGpx = async (file: File) => {
    try {
      const parsed = parseGpx(await file.text());
      const pts = parsed.tracks[0]?.points?.length ? parsed.tracks[0].points : parsed.waypoints;
      if (!pts || pts.length < 2) {
        showToast('GPX needs a start and end point.', 'warning');
        return;
      }
      const first = pts[0]!;
      const last = pts[pts.length - 1]!;
      const nameAt = (p: typeof first, fallback: string) => p.name || fallback;
      setEndpoints([
        {
          id: `ep-gpx-start-${Date.now()}`,
          query: nameAt(first, 'GPX start'),
          place: {
            id: 'gpx-start',
            displayName: nameAt(first, 'GPX start'),
            shortName: nameAt(first, 'GPX start'),
            coordinate: { latitude: first.lat, longitude: first.lon },
          },
          dwellSec: 0,
        },
        {
          id: `ep-gpx-end-${Date.now()}`,
          query: nameAt(last, 'GPX end'),
          place: {
            id: 'gpx-end',
            displayName: nameAt(last, 'GPX end'),
            shortName: nameAt(last, 'GPX end'),
            coordinate: { latitude: last.lat, longitude: last.lon },
          },
          dwellSec: 0,
        },
      ]);
      let withEle = pts;
      if (pts.some((p) => p.ele == null)) {
        const sample = thinElevationPoints(pts);
        const elev = await fetchElevation(sample.map((p) => ({ lat: p.lat, lon: p.lon, ele: p.ele })));
        withEle = withFetchedElevations(sample, elev);
      }
      setElevationStats(computeElevationStats(withEle.map((p) => ({ lat: p.lat, lon: p.lon, ele: p.ele }))));
      showToast('GPX imported. Building route…', 'success');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Could not read GPX.', 'warning');
    }
  };

  // Reminder ticker effect
  useEffect(() => {
    const id = setInterval(() => {
      tickReminders(Date.now(), unitsMode === 'imperial');
    }, 30_000);
    return () => clearInterval(id);
  }, [unitsMode]);

  // Remove splash screen once app is loaded
  useEffect(() => {
    const splash = document.getElementById('splash');
    if (splash) {
      splash.style.opacity = '0';
      setTimeout(() => splash.remove(), 350);
    }
  }, []);

  return (
    <div
      className="app-shell"
      style={{
        position: 'relative',
        width: '100vw',
        overflow: 'hidden',
        backgroundColor: 'var(--bg)',
      }}
    >
      {/* 1. Full-Bleed Map View */}
      <MapView
        routes={routes}
        selectedRouteIndex={selectedRouteIndex}
        onSelectRouteIndex={handleSelectRouteIndex}
        waypoints={waypoints}
        waypointWeather={waypointWeather}
        routeAlerts={alertsByWaypoint}
        activeLayer={activeLayer}
        unitsMode={unitsMode}
        departureTime={effectiveDeparture}
        selectedWaypointId={selectedWaypointId}
        onSelectWaypointId={setSelectedWaypointId}
        weatherLoading={weatherLoadingProgress !== null}
        scrubProgress={scrubProgress}
        isDark={isDark}
        precipTileUrl={precipTileUrl}
        isDesktop={isDesktop}
        endpoints={endpoints}
      />

      <div
        id="ticker-metric-toggle"
        style={{
          position: 'absolute',
          zIndex: 56,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
          ...(isDesktop
            ? { top: 16, left: 392, right: 16 }
            : { top: mobileToggleTop, left: 12, right: 72 }),
        }}
      >
        <div style={{ pointerEvents: 'auto' }}>
          <TickerMetricToggle value={activeLayer} onChange={setActiveLayer} />
        </div>
      </div>

      {selectedWaypoint && (
        <TickerWeatherDetail
          waypoint={selectedWaypoint}
          weather={waypointWeather.get(selectedWaypoint.id)}
          weatherLoading={weatherLoadingProgress !== null}
          alerts={alertsByWaypoint.get(selectedWaypoint.id)}
          unitsMode={unitsMode}
          isNarrow={!useDetailFloat}
          lookingUpLocation={
            isPlaceFallback(selectedWaypoint.placeName) && !localityMiss.has(selectedWaypoint.id)
          }
          onClose={() => setSelectedWaypointId(null)}
        />
      )}

      {/* 2. Top Scrim for iOS status bar blend */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 60,
          background: `linear-gradient(${isDark ? 'rgba(20,20,22,0.45)' : 'rgba(255,255,255,0.48)'}, rgba(0,0,0,0))`,
          backdropFilter: 'blur(5px)',
          WebkitBackdropFilter: 'blur(5px)',
          WebkitMaskImage: 'linear-gradient(#000 0, #000 40%, transparent 100%)',
          maskImage: 'linear-gradient(#000 0, #000 40%, transparent 100%)',
          pointerEvents: 'none',
          zIndex: 40,
        }}
      />

      {/* 3. In-App Toast Notification */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            backgroundColor: isDark ? 'rgba(44,44,46,0.92)' : 'rgba(255,255,255,0.92)',
            color: 'var(--label)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            padding: '10px 18px',
            borderRadius: 22,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18), 0 0 0 0.5px var(--sep)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            pointerEvents: 'auto',
            cursor: 'pointer',
            maxWidth: '90vw',
          }}
          onClick={() => setToast(null)}
        >
          {toast.type === 'success' && <CheckCircle2 size={18} color="#34C759" />}
          {toast.type === 'warning' && <AlertTriangle size={18} color="#FF9500" />}
          {toast.type === 'info' && <Info size={18} color="var(--accent)" />}
          <span className="t-sub" style={{ fontWeight: 600, fontSize: 13 }}>
            {toast.message}
          </span>
        </div>
      )}

      {/* 4. Offline Notice Banner */}
      {isOffline && (
        <div
          style={{
            position: 'absolute',
            top: isDesktop ? 16 : 70,
            left: isDesktop ? 424 : 12,
            right: 12,
            zIndex: 55,
            backgroundColor: isDark ? 'rgba(58,40,20,0.92)' : 'rgba(255,243,224,0.95)',
            border: '1px solid rgba(255,149,0,0.4)',
            color: isDark ? '#FFD60A' : '#B25E00',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            padding: '8px 14px',
            borderRadius: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
          }}
        >
          <WifiOff size={16} />
          <span>You are offline. Live routing and weather updates will resume when reconnected.</span>
        </div>
      )}

      {/* 5. Forecast Horizon Notice Banner */}
      {/* Offline Cached Notice Banner */}
      {offlineCachedAt && (
        <div
          style={{
            position: 'absolute',
            top: isDesktop ? (isOffline ? 60 : 16) : (isOffline ? 116 : 70),
            left: isDesktop ? 424 : 12,
            right: 12,
            zIndex: 54,
            backgroundColor: isDark ? 'rgba(38,38,42,0.92)' : 'rgba(240,240,245,0.95)',
            border: '1px solid var(--sep)',
            color: 'var(--label)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            borderRadius: 12,
            padding: '8px 12px',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
          }}
        >
          <Clock size={16} />
          <span>Showing cached forecast from {offlineCachedAt}.</span>
        </div>
      )}

      {!isOffline && isBeyondForecastHorizon && (
        <div
          style={{
            position: 'absolute',
            top: isDesktop ? 16 : 70,
            left: isDesktop ? 424 : 12,
            right: 12,
            zIndex: 55,
            backgroundColor: isDark ? 'rgba(38,38,42,0.92)' : 'rgba(240,240,245,0.95)',
            border: '1px solid var(--sep)',
            color: 'var(--label)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            padding: '8px 14px',
            borderRadius: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
          }}
        >
          <Clock size={16} color="var(--accent)" />
          <span>Departure is beyond the 16-day forecast horizon. Showing latest available forecast data.</span>
        </div>
      )}

      {/* =========================================================================
          DESKTOP / TABLET SIDEBAR LAYOUT (>= 900px, matches flow/05-ipad-route-*)
         ========================================================================= */}
      {isDesktop ? (
        <aside
          id="map-desktop-sidebar"
          className="sheet-surface"
          style={{
            position: 'absolute',
            left: 16,
            top: 16,
            bottom: 16,
            width: 392,
            borderRadius: 'var(--r-sheet)',
            zIndex: 50,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            padding: 16,
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <div className="t-title2">
                {endpoints[0]?.place?.shortName || 'Start'} to{' '}
                {endpoints[endpoints.length - 1]?.place?.shortName || 'Destination'}
              </div>
              <div className="t-foot c2" style={{ marginTop: 2 }}>
                {activeRoute
                  ? `${formatDistance(activeRoute.distance, unitsMode).primary} · Leave ${formatClockTime(effectiveDeparture)}`
                  : 'Enter start and destination'}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="cbtn" onClick={handleShare} title="Share Trip" aria-label="Share">
                <Share2 size={16} />
              </button>
              <button
                className="cbtn"
                onClick={() => setShowSettingsModal(true)}
                title="Settings"
                aria-label="Settings"
              >
                <Settings size={16} />
              </button>
            </div>
          </div>

          {/* Search Card */}
          <div style={{ marginBottom: 16, position: 'relative', zIndex: 50 }}>
            <SearchCard
              endpoints={endpoints}
              onUpdateEndpoints={setEndpoints}
              onOpenDwellPicker={setDwellPickerIndex}
              onBuildRoute={handleBuildRoute}
              isBuilding={isBuildingRoute}
              weatherLoadingProgress={weatherLoadingProgress}
              canAddStop={endpoints.length < 5}
              onAddStop={handleAddStop}
              onRemoveStop={handleRemoveStop}
              onToast={showToast}
              avoidOptions={avoidOptions}
              onChangeAvoidOptions={setAvoidOptions}
              departureTime={effectiveDeparture}
              onOpenDepartureModal={() => setShowDepartureModal(true)}
            />
            {routeError && (
              <div
                className="card-surface"
                style={{ padding: '8px 12px', marginTop: 6, color: 'var(--danger)', fontSize: 13 }}
              >
                {routeError}
              </div>
            )}
          </div>

          {/* Available Route Cards */}
          {routes.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div
                className="t-foot c2"
                style={{
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  fontSize: 11,
                  marginBottom: 8,
                }}
              >
                Available Routes
              </div>
              <RouteCards
                routes={routes}
                selectedRouteIndex={selectedRouteIndex}
                onSelectRouteIndex={handleSelectRouteIndex}
                departureTime={effectiveDeparture}
                unitsMode={unitsMode}
                isDark={isDark}
                avoidOptions={avoidOptions}
              />
            </div>
          )}

          {/* Drive Chart Card */}
          {activeRoute && waypoints.length > 0 && (
            <div className="card-surface" style={{ padding: '12px 14px', marginBottom: 16 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                  borderBottom: '0.5px solid var(--sep)',
                  paddingBottom: 6,
                }}
              >
                <div className="t-foot c2" style={{ textTransform: 'uppercase', fontWeight: 600 }}>
                  Along Your Drive
                </div>
                <div style={{ display: 'flex', gap: 10 }} className="t-cap c2">
                  <span>°{unitsMode === 'imperial' ? 'F' : 'C'}</span>
                  <span>Rain</span>
                  <span>{unitsMode === 'imperial' ? 'mph' : 'km/h'}</span>
                </div>
              </div>

              <DriveChart
                waypoints={waypoints}
                waypointWeather={waypointWeather}
                routeDurationSec={activeRoute.duration}
                routeDistanceM={activeRoute.distance}
                departureTime={effectiveDeparture}
                scrubProgress={scrubProgress !== null ? scrubProgress : 0.65}
                onScrubChange={(p) => setScrubProgress(p)}
                unitsMode={unitsMode}
                dark={isDark}
                width={360}
              />
            </div>
          )}

          {/* Route Alerts Summary (if any) */}
          {allRouteAlerts.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 6,
                }}
              >
                <span className="t-foot c2" style={{ textTransform: 'uppercase', fontWeight: 600 }}>
                  Alerts on Route ({allRouteAlerts.length})
                </span>
                <button
                  style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer' }}
                  className="t-foot"
                  onClick={() => setShowAlertsModal(true)}
                >
                  Details
                </button>
              </div>

              <div className="cell-group">
                {allRouteAlerts.slice(0, 3).map((alert, i) => (
                  <div
                    key={i}
                    className="cell"
                    style={{ cursor: 'pointer', gap: 10 }}
                    onClick={() => setShowAlertsModal(true)}
                  >
                    <div
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 13,
                        backgroundColor: severityColor(alert.severity),
                        color: '#000',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    >
                      !
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="t-headline" style={{ fontSize: 14 }}>{alert.event}</div>
                      <div className="t-foot c2" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {alert.headline}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pinned Departure Block at Sidebar Bottom */}
          {activeRoute && (
            <div style={{ marginTop: 'auto', paddingTop: 14, borderTop: '0.5px solid var(--sep)' }}>
              {/* Leave / Arrive times */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span className="t-foot c2">Leave at</span>
                <span className="t-foot c2">
                  Arrive · {formatDuration(activeRoute.duration + totalViaDwellSec)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <span className="t-large">{formatClockTime(effectiveDeparture)}</span>
                <span className="t-title2" style={{ fontWeight: 600 }}>{formatClockTime(arrivalTime)}</span>
              </div>

              {/* Verdict Strip */}
              {verdictInfo && (
                <div
                  style={{
                    height: 48,
                    borderRadius: 24,
                    backgroundColor: verdictInfo.bg,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '0 14px',
                    marginBottom: 12,
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>{renderVerdictIcon(verdictInfo.iconType, verdictInfo.color, 20)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="t-headline" style={{ fontSize: 14 }}>{verdictInfo.title}</div>
                    <div className="t-foot c2" style={{ fontSize: 12 }}>{verdictInfo.subtitle}</div>
                  </div>
                </div>
              )}

              {/* Departure Scrubber Trigger */}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn-gray"
                  style={{ flex: 1, height: 42, fontSize: 14 }}
                  onClick={() => setShowDepartureModal(true)}
                >
                  <Clock size={16} style={{ marginRight: 6 }} />
                  <span>Change Departure</span>
                </button>
                <button
                  className="btn-gray"
                  style={{ height: 42, fontSize: 13, padding: '0 12px' }}
                  onClick={handleSaveOriginDest}
                  title="Save origin and destination on this browser"
                  aria-label="Save origin and destination on this browser"
                >
                  Save
                </button>
                <button
                  className="cbtn"
                  style={{ width: 42, height: 42 }}
                  onClick={() => setShowSavedDrawer(true)}
                  title="Saved on this browser"
                  aria-label="Open saved origin and destination pairs"
                >
                  <Bookmark size={18} />
                </button>
              </div>
            </div>
          )}
        </aside>
      ) : (
        /* =========================================================================
            MOBILE LAYOUT (Phone-first, 390x844 viewport, matches leave-*.png)
           ========================================================================= */
        <>
          {/* Top Floating Search Card */}
          <div
            id="map-search-card"
            style={{
              position: 'absolute',
              top: 12,
              left: 12,
              right: 12,
              zIndex: 50,
            }}
          >
            <SearchCard
              endpoints={endpoints}
              onUpdateEndpoints={setEndpoints}
              onOpenDwellPicker={setDwellPickerIndex}
              onBuildRoute={handleBuildRoute}
              isBuilding={isBuildingRoute}
              weatherLoadingProgress={weatherLoadingProgress}
              canAddStop={endpoints.length < 5}
              onAddStop={handleAddStop}
              onRemoveStop={handleRemoveStop}
              onToast={showToast}
              avoidOptions={avoidOptions}
              onChangeAvoidOptions={setAvoidOptions}
              departureTime={effectiveDeparture}
              onOpenDepartureModal={() => setShowDepartureModal(true)}
            />
            {routeError && (
              <div
                className="card-surface"
                style={{ padding: '8px 12px', marginTop: 6, color: 'var(--danger)', fontSize: 13 }}
              >
                {routeError}
              </div>
            )}
          </div>

          {/* Floating Map Controls (Top Right stack) */}
          <div
            id="map-control-rail"
            style={{
              position: 'absolute',
              top: 128,
              right: 12,
              zIndex: 45,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              alignItems: 'flex-end',
            }}
          >
            {/* Hourly Precipitation Overlay Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                className="cbtn glass"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  color: precipEnabled ? 'var(--accent)' : 'var(--label2)',
                }}
                onClick={() => setPrecipEnabled(!precipEnabled)}
                title="Toggle Hourly Precipitation Overlay"
                aria-label="Precipitation Overlay"
              >
                <Droplets size={18} />
              </button>
            </div>

            {/* Route Alerts Button (if alerts present) */}
            {allRouteAlerts.length > 0 && (
              <button
                className="cbtn glass"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  color: severityColor(worstRouteSeverity || 'Moderate'),
                  position: 'relative',
                }}
                onClick={() => setShowAlertsModal(true)}
                title="Route Alerts"
                aria-label="Alerts"
              >
                <AlertTriangle size={18} />
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    minWidth: 16,
                    height: 16,
                    borderRadius: 8,
                    backgroundColor: 'var(--danger)',
                    color: '#FFF',
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {allRouteAlerts.length}
                </span>
              </button>
            )}

            {/* Settings Button */}
            <button
              className="cbtn glass"
              style={{ width: 44, height: 44, borderRadius: 22 }}
              onClick={() => setShowSettingsModal(true)}
              title="Settings"
              aria-label="Settings"
            >
              <Settings size={18} />
            </button>
          </div>

          {/* Bottom Sheet for Mobile */}
          {activeRoute && !selectedWaypoint && (
            <div
              id="map-bottom-sheet"
              className="sheet-surface"
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                borderTopLeftRadius: 38,
                borderTopRightRadius: 38,
                padding: '12px 16px 28px',
                zIndex: 48,
                maxHeight: '48vh',
                overflowY: 'auto',
                boxSizing: 'border-box',
              }}
            >
              {/* Grabber */}
              <div
                style={{
                  width: 36,
                  height: 5,
                  borderRadius: 2.5,
                  backgroundColor: 'var(--label3)',
                  margin: '0 auto 10px',
                }}
              />

              {/* Trip Header & Tools */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                <div>
                  <div className="t-title2">
                    {endpoints[0]?.place?.shortName || 'LA'} to{' '}
                    {endpoints[endpoints.length - 1]?.place?.shortName || 'Vegas'}
                  </div>
                  <div className="t-foot c2">
                    {formatDistance(activeRoute.distance, unitsMode).primary} ·{' '}
                    <span
                      style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => setShowDepartureModal(true)}
                    >
                      Leave {formatClockTime(effectiveDeparture)}
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="cbtn" onClick={handleShare} title="Share">
                    <Share2 size={15} />
                  </button>
                  <button className="cbtn" style={{ width: 'auto', padding: '0 8px', fontSize: 12, fontWeight: 700 }} onClick={handleSaveOriginDest} title="Save origin and destination on this browser" aria-label="Save origin and destination on this browser">
                    Save
                  </button>
                  <button className="cbtn" onClick={() => setShowSavedDrawer(true)} title="Saved on this browser" aria-label="Open saved origin and destination pairs">
                    <Bookmark size={15} />
                  </button>
                </div>
              </div>

              {/* Verdict Strip */}
              {verdictInfo && (
                <div
                  style={{
                    height: 44,
                    borderRadius: 22,
                    backgroundColor: verdictInfo.bg,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '0 12px',
                    marginBottom: 10,
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>{renderVerdictIcon(verdictInfo.iconType, verdictInfo.color, 18)}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="t-headline" style={{ fontSize: 13 }}>{verdictInfo.title}</div>
                    <div className="t-foot c2" style={{ fontSize: 11 }}>{verdictInfo.subtitle}</div>
                  </div>
                </div>
              )}

              {/* Drive Chart */}
              {waypoints.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <DriveChart
                    waypoints={waypoints}
                    waypointWeather={waypointWeather}
                    routeDurationSec={activeRoute.duration}
                    routeDistanceM={activeRoute.distance}
                    departureTime={effectiveDeparture}
                    scrubProgress={scrubProgress !== null ? scrubProgress : 0.65}
                    onScrubChange={(p) => setScrubProgress(p)}
                    unitsMode={unitsMode}
                    dark={isDark}
                    width={windowWidth - 32}
                  />
                </div>
              )}

              {/* Bottom Departure / Arrival Bar */}
              <div
                style={{
                  height: 48,
                  borderRadius: 24,
                  backgroundColor: 'var(--fill2)',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 16px',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div className="t-cap2 c2" style={{ textTransform: 'uppercase' }}>Departure</div>
                  <div className="t-headline" style={{ fontSize: 14 }}>{formatClockTime(effectiveDeparture)}</div>
                </div>

                <button
                  onClick={() => setShowDepartureModal(true)}
                  style={{
                    background: 'var(--accent)',
                    color: '#FFF',
                    border: 'none',
                    borderRadius: 14,
                    padding: '5px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Scrub Time
                </button>

                <div style={{ textAlign: 'right' }}>
                  <div className="t-cap2 c2" style={{ textTransform: 'uppercase' }}>Arrival</div>
                  <div className="t-headline" style={{ fontSize: 14 }}>{formatClockTime(arrivalTime)}</div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* =========================================================================
          MODALS & DRAWERS
         ========================================================================= */}

      {/* 1. Departure Scrubber Sheet */}
      {showDepartureModal && (
        <DepartureTimeline
          departureTime={confirmedDepartureTime}
          onConfirm={(d) => {
            setConfirmedDepartureTime(d);
            setPreviewDepartureTime(null);
          }}
          onPreviewChange={setPreviewDepartureTime}
          onClose={() => {
            setPreviewDepartureTime(null);
            setShowDepartureModal(false);
          }}
          bestLeave={bestLeave ? bestLeave.departure : null}
        />
      )}

      {/* 2. Stop Dwell Time Picker */}
      {dwellPickerIndex !== null && endpoints[dwellPickerIndex] && (
        <StopDwellPickerModal
          stopLetter={String.fromCharCode(65 + dwellPickerIndex)}
          initialDwellSec={endpoints[dwellPickerIndex]!.dwellSec}
          arrivalMs={effectiveDeparture.getTime() + 3600 * 1000}
          onConfirm={(dwellSec) => {
            const next = [...endpoints];
            next[dwellPickerIndex] = { ...next[dwellPickerIndex]!, dwellSec };
            setEndpoints(next);
          }}
          onClose={() => setDwellPickerIndex(null)}
        />
      )}

      {/* 3. Route Alerts Modal (severe info stays here, not in a map-blocking waypoint sheet) */}
      {showAlertsModal && (
        <RouteAlertsModal
          alerts={allRouteAlerts}
          onClose={() => setShowAlertsModal(false)}
        />
      )}

      {/* 5. Saved Routes Drawer */}
      {showSavedDrawer && (
        <SavedRoutesDrawer
          routes={listSavedRoutes()}
          onSelectRoute={handleLoadSavedRoute}
          onDeleteRoute={(id) => {
            deleteSavedRoute(id);
            setSavedRoutesVersion((v) => v + 1);
          }}
          onClose={() => setShowSavedDrawer(false)}
        />
      )}

      {/* 6. Settings Modal */}
      {showSettingsModal && (
        <SettingsModal
          unitsMode={unitsMode}
          onToggleUnits={handleToggleUnits}
          themeMode={themeMode}
          onSetThemeMode={setThemeMode}
          precipEnabled={precipEnabled}
          onTogglePrecip={() => setPrecipEnabled(!precipEnabled)}
          reminderEnabled={reminderEnabled}
          reminderLeadMin={reminderLeadMin}
          onToggleReminder={async () => {
            const next = !reminderEnabled;
            if (next) {
              const ok = await ensureNotificationPermission();
              if (!ok) {
                showToast('Enable browser notifications to receive leave reminders.', 'warning');
                return;
              }
              if (activeRoute && endpoints[0]?.place && endpoints[endpoints.length - 1]?.place) {
                const watch = watchFromCurrentTrip({
                  name: `${endpoints[0]!.place!.shortName} to ${endpoints[endpoints.length - 1]!.place!.shortName}`,
                  destinationName: endpoints[endpoints.length - 1]!.place!.shortName,
                  departAt: effectiveDeparture.getTime(),
                  durationSec: activeRoute.duration,
                  reminderLeadMin,
                  imperial: unitsMode === 'imperial',
                });
                const existing = loadWatches().filter((w) => w.name !== watch.name);
                saveWatches([...existing, watch]);
              }
            }
            setReminderEnabled(next);
          }}
          onSetReminderLead={(min) => setReminderLeadMin(min)}
          onImportGpx={handleImportGpx}
          elevationStats={elevationStats}
          onClearRoute={() => {
            setEndpoints([
              { ...endpoints[0]!, query: '', place: null, dwellSec: 0 },
              { id: `ep-${Date.now()}`, query: '', place: null, dwellSec: 0 },
            ]);
            setRoutes([]);
            setWaypoints([]);
          }}
          onSaveRoute={handleSaveOriginDest}
          canSaveRoute={Boolean(endpoints[0]?.place && endpoints[endpoints.length - 1]?.place)}
          onClose={() => setShowSettingsModal(false)}
        />
      )}

      {/* 7. Onboarding Modal on First Launch */}
      {showOnboarding && <OnboardingModal onDismiss={handleDismissOnboarding} />}
    </div>
  );
};
